import type { ResourceCategory } from '@/mocks/resources';
import { hotTags } from '@/mocks/resources';

interface HeroProps {
  query: string;
  onQuery: (v: string) => void;
  categories: ResourceCategory[];
  onTag: (v: string) => void;
}

const Hero = ({ query, onQuery, categories, onTag }: HeroProps) => {
  const total = categories.reduce((sum, c) => sum + c.list.length, 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const el = document.getElementById('directory');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative overflow-hidden border-b border-background-200 bg-background-100 pt-16">
      <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-primary-100/60 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -left-20 bottom-0 h-72 w-72 rounded-full bg-accent-100/70 blur-3xl" aria-hidden="true" />

      <div className="relative mx-auto flex max-w-7xl flex-col items-center px-4 py-14 text-center md:px-6 md:py-20">
        <span className="mb-5 inline-flex items-center gap-1.5 rounded-full border border-accent-200 bg-accent-50 px-3 py-1 text-xs text-accent-800">
          <i className="ri-sparkling-line text-sm" aria-hidden="true" />
          按分类汇总 · 每周更新 · 免费收录
        </span>

        <h1 className="max-w-3xl font-heading text-3xl font-black leading-tight text-foreground-950 md:text-5xl">
          资源太多找不到？来 51全网
          <span className="block text-primary-600">按分类一站式找资源</span>
        </h1>

        <p className="mt-5 max-w-2xl text-sm leading-7 text-foreground-700 md:text-base">
          我们把工具、模板、素材、资料合集与常用网站按分类整理成一张清单。
          打开即可按需查找，找到能用的资源，点一下就能收藏，不用再重新整理收藏夹。
        </p>

        {/* 搜索 */}
        <form
          onSubmit={handleSubmit}
          className="mt-8 flex w-full max-w-2xl items-center gap-2 rounded-full border border-background-200 bg-background-50 p-1.5 pl-4 focus-within:border-primary-400"
          role="search"
        >
          <i className="ri-search-line text-lg text-foreground-500" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="搜索资源，如：简历模板、AI 写作、图标素材…"
            className="min-w-0 flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
            aria-label="搜索资源"
          />
          {query && (
            <button
              type="button"
              onClick={() => onQuery('')}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-foreground-500 hover:text-primary-600"
              aria-label="清空搜索"
            >
              <i className="ri-close-circle-fill text-lg" aria-hidden="true" />
            </button>
          )}
          <button
            type="submit"
            className="flex h-9 shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-4 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            找资源
            <i className="ri-arrow-right-line text-base" aria-hidden="true" />
          </button>
        </form>

        {/* 热门标签 */}
        <div className="mt-5 flex w-full max-w-3xl flex-wrap items-center justify-center gap-2">
          <span className="text-xs text-foreground-600">大家都在找：</span>
          {hotTags.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onTag(t)}
              className="whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              {t}
            </button>
          ))}
        </div>

        {/* 数据概览 */}
        <dl className="mt-9 grid w-full max-w-3xl grid-cols-3 gap-3 md:gap-6">
          <div className="rounded-lg bg-background-50/80 px-3 py-4 md:px-6 md:py-5">
            <dt className="text-xs text-foreground-600">收录分类</dt>
            <dd className="mt-1 font-heading text-2xl font-bold text-primary-600 md:text-3xl">{categories.length} 类</dd>
          </div>
          <div className="rounded-lg bg-background-50/80 px-3 py-4 md:px-6 md:py-5">
            <dt className="text-xs text-foreground-600">优质资源</dt>
            <dd className="mt-1 font-heading text-2xl font-bold text-primary-600 md:text-3xl">{total}+ 项</dd>
          </div>
          <div className="rounded-lg bg-background-50/80 px-3 py-4 md:px-6 md:py-5">
            <dt className="text-xs text-foreground-600">更新频率</dt>
            <dd className="mt-1 font-heading text-2xl font-bold text-primary-600 md:text-3xl">每周</dd>
          </div>
        </dl>
      </div>
    </section>
  );
};

export default Hero;